/**
 * PDF Template Management Page
 * Admin page for managing PDF templates and their mappings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { TemplateMapper } from './TemplateMapper';
import {
  PdfTemplateListItem,
  PdfTemplateWithMappings,
  MappingRegion,
} from './types';

const API_BASE = '/api/pdf-templates';

interface TemplateStats {
  totalTemplates: number;
  activeTemplates: number;
  templatesWithMappings: number;
  totalUsage: number;
  bankCounts: Record<string, number>;
}

export const PdfTemplateManagementPage: React.FC = () => {
  // State
  const [templates, setTemplates] = useState<PdfTemplateListItem[]>([]);
  const [stats, setStats] = useState<TemplateStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingTemplate, setEditingTemplate] = useState<PdfTemplateWithMappings | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadBankName, setUploadBankName] = useState('');
  const [uploadTemplateName, setUploadTemplateName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      const [templatesRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}?includeInactive=true`),
        fetch(`${API_BASE}/stats`),
      ]);

      if (!templatesRes.ok || !statsRes.ok) {
        throw new Error('Failed to load templates');
      }

      const templatesData = await templatesRes.json();
      const statsData = await statsRes.json();

      setTemplates(templatesData.templates);
      setStats(statsData);
      setError(null);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Load template for editing
  const handleEditTemplate = async (id: string) => {
    try {
      const res = await fetch(`${API_BASE}/${id}`);
      if (!res.ok) throw new Error('Failed to load template');

      const data = await res.json();
      setEditingTemplate(data.template);
    } catch (err) {
      console.error('Error loading template:', err);
      alert('Failed to load template');
    }
  };

  // Save mappings
  const handleSaveMappings = async (mappings: Omit<MappingRegion, 'id' | 'isNew'>[]) => {
    if (!editingTemplate) return;

    const res = await fetch(`${API_BASE}/${editingTemplate.id}/mappings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mappings }),
    });

    if (!res.ok) {
      throw new Error('Failed to save mappings');
    }

    // Reload the template to get updated mappings with IDs
    const templateRes = await fetch(`${API_BASE}/${editingTemplate.id}`);
    const templateData = await templateRes.json();
    setEditingTemplate(templateData.template);

    // Reload the list
    loadTemplates();
  };

  // Upload template
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!uploadFile || !uploadBankName || !uploadTemplateName) {
      alert('Please fill in all required fields');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('bankName', uploadBankName);
      formData.append('templateName', uploadTemplateName);
      formData.append('description', uploadDescription);

      const res = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Upload failed');
      }

      // Reset form
      setUploadFile(null);
      setUploadBankName('');
      setUploadTemplateName('');
      setUploadDescription('');
      setShowUploadModal(false);

      // Reload templates
      loadTemplates();
    } catch (err: any) {
      console.error('Error uploading template:', err);
      alert(err.message || 'Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  // Toggle template active status
  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !currentStatus }),
      });

      if (!res.ok) throw new Error('Failed to update template');

      loadTemplates();
    } catch (err) {
      console.error('Error updating template:', err);
      alert('Failed to update template');
    }
  };

  // Delete template
  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
      });

      if (!res.ok) throw new Error('Failed to delete template');

      loadTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      alert('Failed to delete template');
    }
  };

  // If editing, show the mapper
  if (editingTemplate) {
    return (
      <TemplateMapper
        template={editingTemplate}
        onSave={handleSaveMappings}
        onBack={() => setEditingTemplate(null)}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">PDF Templates</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage PDF statement templates for overlay generation
              </p>
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Upload Template
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Templates</div>
              <div className="text-2xl font-bold text-gray-900">{stats.totalTemplates}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{stats.activeTemplates}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">With Mappings</div>
              <div className="text-2xl font-bold text-blue-600">{stats.templatesWithMappings}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Usage</div>
              <div className="text-2xl font-bold text-purple-600">{stats.totalUsage}</div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Templates Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bank / Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pages
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mappings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.map(template => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{template.bankName}</div>
                    <div className="text-sm text-gray-500">{template.templateName}</div>
                    {template.description && (
                      <div className="text-xs text-gray-400 mt-1">{template.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {template.pageCount} page{template.pageCount !== 1 ? 's' : ''}
                  </td>
                  <td className="px-6 py-4">
                    {template.mappingComplete ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {template.mappingCount} fields
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Not mapped
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleActive(template.id, template.isActive)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        template.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {template.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right space-x-3">
                    <button
                      onClick={() => handleEditTemplate(template.id)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit Mappings
                    </button>
                    <a
                      href={`${API_BASE}/${template.id}/download`}
                      className="text-gray-600 hover:text-gray-800 text-sm"
                    >
                      Download
                    </a>
                    <button
                      onClick={() => handleDelete(template.id, template.templateName)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {templates.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p>No PDF templates yet</p>
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="mt-4 text-blue-600 hover:text-blue-800"
                    >
                      Upload your first template
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <form onSubmit={handleUpload}>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload PDF Template</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PDF File *
                    </label>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={e => setUploadFile(e.target.files?.[0] || null)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Name *
                    </label>
                    <input
                      type="text"
                      value={uploadBankName}
                      onChange={e => setUploadBankName(e.target.value)}
                      placeholder="e.g., Chase, Bank of America"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template Name *
                    </label>
                    <input
                      type="text"
                      value={uploadTemplateName}
                      onChange={e => setUploadTemplateName(e.target.value)}
                      placeholder="e.g., Checking Account 2024"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={uploadDescription}
                      onChange={e => setUploadDescription(e.target.value)}
                      placeholder="Optional description"
                      rows={2}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {uploading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    'Upload'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfTemplateManagementPage;
